import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { loadTossPayments } from '@tosspayments/payment-sdk';
import { v4 as uuidv4 } from 'uuid';
import axiosClient from "../../libs/axiosClient";
import TermsDetail from "../../components/TermsDetail/TermsDetail";
import HeaderMain from "../../components/HeaderMain/HeaderMain";
import HeaderMobile from "../../components/HeaderMobile/HeaderMobile";
import Footer from "../../components/Footer/Footer";
import styles from "./Order.module.scss";
import bottomArrow from "../../assets/pay/bottomArrow.png";
import naver from "../../assets/pay/naver.png";
import kakao from "../../assets/pay/kakao.png";
import smile from "../../assets/pay/smile.png";
import payco from "../../assets/pay/payco.png";
import apple from "../../assets/pay/apple.png";
import card from "../../assets/pay/card.png";
import phone from "../../assets/pay/phone.png";
import noBank from "../../assets/pay/noBank.png";

function Order() {
  const [isMobile, setIsMobile] = useState(false);
  const [selectedTab, setSelectedTab] = useState("consultation");
  const [orderData, setOrderData] = useState<any>(null);
  const [userInfo, setUserInfo] = useState<any>(null);
  const [isOrderDetailsOpen, setIsOrderDetailsOpen] = useState(true);
  const [isPayerInfoOpen, setIsPayerInfoOpen] = useState(true);
  const [isPaymentDetailsOpen, setIsPaymentDetailsOpen] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState<"CARD" | "FOREIGN_EASY_PAY" | "TRANSFER" | "VIRTUAL_ACCOUNT" | "MOBILE_PHONE" | "CULTURE_GIFT_CERTIFICATE" >("FOREIGN_EASY_PAY");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [agreementType, setAgreementType] = useState<string | null>(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
    
    const savedLanguage = localStorage.getItem("selectedLanguage") || "en";
    if (i18n.language !== savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };

  }, [i18n]);
  
  useEffect(() => {
    const storedOrderData = sessionStorage.getItem("currentOrder");

    if (storedOrderData) {
      setOrderData(JSON.parse(storedOrderData));
    } else {
      alert("No order data found. Redirecting to treatment list.");
      navigate("/treatment-list");
    }
  }, [navigate]);
  
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await axiosClient.get("/api/v1/users/mypage", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        setUserInfo(response.data);
      } catch (error) {
        console.error("Failed to fetch user info:", error);
      }
    };
  
    fetchUserInfo();
  }, []);
  
  async function handleSubmit() {
    if (!orderData || !userInfo) return;
  
    const payload = {
      currency_code: orderData.currency_code,
      total_price: orderData.total_price,
      hospital_id: orderData.hospital_id,
      order_products: orderData.order_products.map((product: any) => ({
        hospital_id: product.hospital_id,
        product_id: product.product_id,
        option_id: product.option_id,
        quantity: product.quantity,
      })),
    };
  
    try {
      const response = await axiosClient.post("/api/v1/order", payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const orderResponse = response.data;
      requestTossPayment(orderResponse, paymentMethod);
    } catch (error) {
      console.error("주문 데이터 생성 실패: ", error);
      alert("Your order failed. Please try again.");
    }
  }
  
  const requestTossPayment = async (orderResponse: any, paymentMethod: string) => {
    const clientKey = "test_ck_kYG57Eba3G9Z1E60YD69rpWDOxmA";

    try {
      const successUrl = `${window.location.origin}/order/success`;
      const failUrl = `${window.location.origin}/order/fail`;
      const customerKey = userInfo?.uid || uuidv4();
      const tossPayments = new (window as any).TossPayments(clientKey);
      const payment = tossPayments.payment({
        customerKey,
      });

      const paymentData: any = {
        method: paymentMethod,
        amount: {
          currency: orderResponse.currency_code || "USD",
          value: parseFloat(orderResponse.total_price),
        },
        orderId: orderResponse.order_num,
        orderName: orderResponse.title +  " " + t("order.reservationFee"),
        successUrl,
        failUrl,
        customerEmail: userInfo.email,
        customerName: `${userInfo.first_name} ${userInfo.last_name}`,
      };

      switch (paymentMethod) {
        case "CARD":
          paymentData.amount = {
            currency: "KRW",
            value: parseFloat(orderResponse.total_price),
          }
          paymentData.card = {
            useEscrow: false,
            flowMode: "DEFAULT",
            useCardPoint: false,
            useAppCardOnly: false,
          };
          break;

        case "FOREIGN_EASY_PAY":
          paymentData.amount = {
            currency: "USD",
            value: parseFloat(orderResponse.total_price),
          }
          paymentData.foreignEasyPay = {
            provider: "PAYPAL",
            country: "KR",
          };
          break;

        case "TRANSFER":
          paymentData.transfer = {
            cashReceipt: { type: "소득공제" },
            useEscrow: false,
          };
          break;

        case "VIRTUAL_ACCOUNT":
          paymentData.virtualAccount = {
            cashReceipt: { type: "소득공제" },
            useEscrow: false,
            validHours: 24,
          };
          break;

        case "MOBILE_PHONE":
        case "CULTURE_GIFT_CERTIFICATE":
          break;

        default:
          alert("유효하지 않은 결제 방식입니다.");
          return;
      }
      
      await payment.requestPayment(paymentData);
    } catch (error) {
        console.error("Toss Payment Failed:", error);
        alert("Your order failed. Please try again.");
    }
  };
  
  const agreementContents = {
    personalInfo: t("termsDetail.terms1"),
    sensitiveInfo: t("termsDetail.terms2"),
    thirdPartiesInfo: t("termsDetail.terms3"),
  };
  
  const openModal = (type: string) => {
    setScrollPosition(window.scrollY);
    setAgreementType(type);
    setIsModalOpen(true);
  };
  
  const closeModal = () => {
    setIsModalOpen(false);
    window.scrollTo(0, scrollPosition);
  }
  
  return (
    <>
      {isMobile ? (
        <HeaderMobile />
      ) : (
        <HeaderMain selectedTab={selectedTab} onTabChange={setSelectedTab} />
      )}
      <div className={styles.orderContainer}>
        <section className={styles.orderSection}>
          <div
            className={`${styles.sectionHeader} ${isOrderDetailsOpen ? styles.open : ""}`}
            onClick={() => setIsOrderDetailsOpen(!isOrderDetailsOpen)}
          >
            <h2>
              {t("order.orderDetails")}
            </h2>
            <img src={bottomArrow} alt="Toggle" />
          </div>
          
          <div className={styles.divider}></div>
          
          {isOrderDetailsOpen && orderData && (
            <div className={styles.orderDetails}>
              <div className={styles.profileSection}>
                <img src={orderData.profile_image} alt="Profile" className={styles.profileImage} />
                <div className={styles.profileInfo}>
                  <h3>
                    {orderData.hospital_name} <br />
                    <p className={styles.fee}>{t("order.reservationFee")}</p>
                  </h3>
                  <p>{orderData.address} | {orderData.subway}</p>
                </div>
              </div>
              {orderData.order_products.map((product: any, index: number) => (
                <div key={index} className={styles.serviceItem}>
                  <div>
                    <p>{product.title}</p>
                    <span>
                      ${parseFloat(product.price).toLocaleString()} / {t("order.quantityInfo", { quantity: product.quantity })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
        
        <div className={styles.thickDivider}></div>
        
        <section className={styles.orderSection}>
          <div
            className={`${styles.sectionHeader} ${isPayerInfoOpen ? styles.open : ''}`}
            onClick={() => setIsPayerInfoOpen(!isPayerInfoOpen)}
          >
            <h2>{t('order.payer')}</h2>
            <img src={bottomArrow} alt="Toggle" />
          </div>
          <div className={styles.divider}></div>
          {isPayerInfoOpen && userInfo && (
            <div className={styles.payerInfo}>
              <div>
                <strong>{t('order.name')}</strong>
                <span>{userInfo.first_name} {userInfo.last_name}</span>
              </div>
              <div>
                <strong>{t('order.phone')}</strong>
                <span>{userInfo.phone}</span>
              </div>
              <div>
                <strong>{t('order.messenger')}</strong>
                <span>{userInfo.messenger}</span>
              </div>
              <div>
                <strong>{t('order.messengerId')}</strong>
                <span>{userInfo.messenger_id}</span>
              </div>
            </div>
          )}
        </section>
        
        <div className={styles.thickDivider}></div>
        
        <section className={styles.orderSection}>
          <div
            className={`${styles.paymentHeader} ${isPaymentDetailsOpen ? styles.open : ''}`}
            onClick={() => setIsPaymentDetailsOpen(!isPaymentDetailsOpen)}
          >
            <h2>{t('order.method')}</h2>
            <img src={bottomArrow} alt="Toggle" />
          </div>
          <div className={styles.divider}></div>
          {isPaymentDetailsOpen && (
            <div className={styles.paymentTabs}>
              <button
                className={paymentMethod === 'FOREIGN_EASY_PAY' ? styles.active : ''}
                onClick={() => setPaymentMethod('FOREIGN_EASY_PAY')}
              >
                {t('order.overseasCard')}
              </button>
              <button
                className={paymentMethod === 'CARD' ? styles.active : ''}
                onClick={() => setPaymentMethod('CARD')}
              >
                {t('order.generalPayment')}
              </button>
            {/* <button
              className={paymentMethod === 'TRANSFER' ? styles.active : ''}
              onClick={() => setPaymentMethod('TRANSFER')}
            >
              {t('order.transfer')}
            </button>
            <button
              className={paymentMethod === 'VIRTUAL_ACCOUNT' ? styles.active : ''}
              onClick={() => setPaymentMethod('VIRTUAL_ACCOUNT')}
            >
              {t('order.virtualAccount')}
            </button>
            <button
              className={paymentMethod === 'MOBILE_PHONE' ? styles.active : ''}
              onClick={() => setPaymentMethod('MOBILE_PHONE')}
            >
              {t('order.phonePay')}
            </button>
            <button
              className={paymentMethod === 'CULTURE_GIFT_CERTIFICATE' ? styles.active : ''}
              onClick={() => setPaymentMethod('CULTURE_GIFT_CERTIFICATE')}
            >
              {t('order.cultureGift')}
            </button> */}
            </div>  
          )}
          {/* {isPaymentDetailsOpen && (
            <div className={styles.paymentDetails}>
              <p>{t('order.supportText')}</p>
              <div className={styles.paymentIcons}>
                <img src={naver} alt="N Pay" />
                <img src={kakao} alt="Kakao Pay" />
                <img src={smile} alt="SmilePay" />
                <img src={payco} alt="Payco" />
                <img src={apple} alt="Apple" />
                <div className={styles.iconWithLabel}>
                  <img src={card} alt="Credit Card" />
                  <span>{t('order.creditCard')}</span>
                </div>
                <div className={styles.iconWithLabel}>
                  <img src={phone} alt="Mobile Payment" />
                  <span>{t('order.phonePay')}</span>
                </div>
                <div className={styles.iconWithLabel}>
                  <img src={noBank} alt="No Bankbook" />
                  <span>{t('order.noBank')}</span>
                </div>
              </div>
            </div>
          )} */}
        </section>
        
        <div className={styles.thickDivider}></div>
        
        <section className={styles.orderSummary}>
          <h2>{t('order.amount')}</h2>
          {orderData && (
            <>
              <div className={styles.summaryRow}>
                <span>{t('order.orderAmount')}</span>
                <span>
                  ${parseFloat(orderData.total_price).toLocaleString()} 
                </span>
              </div>
              <div className={styles.summaryRow}>
                <span>{t('order.discountAmount')}</span>
                <span className={styles.discount}>-$0</span>
              </div>
              <div className={styles.divider}></div>
              <div className={styles.total}>
                <h3>{t('order.finalAmount')}</h3>
                <span>
                  ${parseFloat(orderData.total_price).toLocaleString()}
                </span>
              </div>
            </>
          )}
        </section>
        <section className={styles.agreementSection}>
          <div className={styles.notice}>
            <ul>
              <li>{t('order.terms1')}</li>
              <li>{t('order.terms2')}</li>
              <li>{t('order.terms3')}</li>
            </ul>
          </div>
          <div className={styles.agreementList}>
            <div>
              <span>{t('order.personalInfo')}</span>
              <a href="#" onClick={(e) => { 
                e.preventDefault(); 
                openModal("personalInfo"); 
              }}>
                  {t('order.view')}
              </a>
            </div>
            <div>
              <span>{t('order.sensitiveInfo')}</span>
              <a href="#" onClick={(e) => { 
                e.preventDefault(); 
                openModal("sensitiveInfo"); 
              }}>
                {t('order.view')}
              </a>
            </div>
            <div>
              <span>{t('order.thirdPartiesInfo')}</span>
              <a href="#" onClick={(e) => { 
                e.preventDefault(); 
                openModal("thirdPartiesInfo"); 
              }}>
                  {t('order.view')}
              </a>
            </div>
          </div>
          <div className={styles.divider}></div>
          <p className={styles.confirmText}>{t('order.confirmText')}</p>
        </section>
        <button 
          className={styles.buyNowBtn}
          onClick={handleSubmit}
        >
          {t('order.buy')}
        </button>
      </div>
      <Footer />
      <TermsDetail
        isOpen={isModalOpen}
        onClose={closeModal}
        title={t(`order.${agreementType}`)}
        content={agreementType ? agreementContents[agreementType as keyof typeof agreementContents] : ""}
      />
    </>
  );
}

export default Order;
